/**
 * @file validator.js
 * @description Utility module containing validation functions and error handling for the registration process.
 * @module validator
 */
/**
 * Custom Error class for validation errors with a code.
 */
export class ValidationError extends Error {
    constructor(message, code) {
        super(message);
        this.name = "ValidationError";
        this.code = code;
    }
}

/**
 * Calculate a person's age in years.
 *
 * @param {object} p An object representing a person, implementing a birth Date parameter.
 * @return {number} The age in years of p.
 * @throws {ValidationError} If params are missing, invalid type, or age < 18.
 */
export function calculateAge(p) {
    if (!p) {
        throw new ValidationError("missing param p", "MISSING_PARAM")
    }
    if (typeof p !== 'object') {
        throw new ValidationError("param p must be an object", "INVALID_TYPE")
    }
    if (!p.birth) {
        throw new ValidationError("missing param p.birth", "MISSING_BIRTH")
    }
    if (!(p.birth instanceof Date)) {
        throw new ValidationError("p.birth must be a Date object", "INVALID_BIRTH_TYPE")
    }
    if (isNaN(p.birth)) {
        throw new ValidationError("p.birth is an invalid Date", "INVALID_DATE")
    }
    if (p.birth.getFullYear() < 1900) {
        throw new ValidationError("Birth year must be 1900 or later.", "INVALID_YEAR")
    }

    const today = new Date();
    let age = today.getFullYear() - p.birth.getFullYear();
    const m = today.getMonth() - p.birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < p.birth.getDate())) {
        age--;
    }
    if (age < 18) {
        throw new ValidationError("Age must be at least 18 years old.", "MINOR")
    }
    return age;
}

/**
 * Validate a french postal code.
 *
 * @param {string} code The postal code to check.
 * @return {boolean} True if the postal code is valid.
 * @throws {ValidationError} If code is missing, not a string, or invalid format.
 */
export function validatePostalCode(code) {
    if (!code) {
        throw new ValidationError("missing param code", "MISSING_CODE")
    }
    if (typeof code !== 'string') {
        throw new ValidationError("code must be a string", "INVALID_TYPE")
    }
    if (!/^\d{5}$/.test(code)) {
        throw new ValidationError("code must be 5 digits", "INVALID_FORMAT")
    }
    return true;
}

/**
 * Validate a person's identity (name/surname).
 *
 * @param {string} name The name to check.
 * @return {boolean} True if the name is valid.
 * @throws {ValidationError} If name is missing, not a string, or contains invalid characters.
 */
export function validateIdentity(name) {
    if (!name) {
        throw new ValidationError("missing param name", "MISSING_NAME")
    }
    if (typeof name !== 'string') {
        throw new ValidationError("name must be a string", "INVALID_TYPE")
    }
    if (!/^[a-zA-Z\u00C0-\u00FF-]+$/.test(name)) {
        throw new ValidationError("name must only contain letters, accents and hyphens", "INVALID_FORMAT")
    }
    return true;
}

/**
 * Validate an email address.
 *
 * @param {string} email The email to check.
 * @param {Array} [existingUsers] Optional list of existing users to check against.
 * @return {boolean} True if the email is valid.
 * @throws {ValidationError} If email is missing, not a string, invalid format, or already exists.
 */
export function validateEmail(email, existingUsers = null) {
    if (!email) {
        throw new ValidationError("missing param email", "MISSING_EMAIL")
    }
    if (typeof email !== 'string') {
        throw new ValidationError("email must be a string", "INVALID_TYPE")
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new ValidationError("email is invalid", "INVALID_FORMAT")
    }

    // Check uniqueness against provided list OR localStorage (fallback)
    let usersToCheck = existingUsers;

    if (!usersToCheck) {
        try {
            usersToCheck = getFromLocalStorage();
        } catch (error) {
            usersToCheck = [];
        }
    }

    if (usersToCheck.some(user => user.email === email)) {
        throw new ValidationError("email already exists", "EMAIL_EXISTS");
    }
    return true;
}

/**
 * Validate a city name.
 *
 * @param {string} city The city to check.
 * @return {boolean} True if the city is valid.
 * @throws {ValidationError} If city is missing, not a string, or contains invalid characters.
 */
export function validateCity(city) {
    if (!city) {
        throw new ValidationError("missing param city", "MISSING_CITY");
    }
    if (typeof city !== 'string') {
        throw new ValidationError("city must be a string", "INVALID_TYPE");
    }
    if (!/^[a-zA-Z\u00C0-\u00FF\s'-]+$/.test(city)) {
        throw new ValidationError("city must only contain letters, accents, spaces, hyphens and apostrophes", "INVALID_FORMAT");
    }
    return true;
}

/**
 * Save data to localStorage.
 *
 * @param {Object} formData The data to save.
 * @return {boolean} True if the save was successful.
 * @throws {ValidationError} If localStorage access fails.
 */
export function saveToLocalStorage(formData) {
    try {
        const existingData = JSON.parse(localStorage.getItem('registrations') || '[]');
        existingData.push({
            ...formData,
            timestamp: new Date().toISOString()
        });
        localStorage.setItem('registrations', JSON.stringify(existingData));
        return true;
    } catch (error) {
        throw new ValidationError("Failed to save data to localStorage", "STORAGE_ERROR");
    }
}

/**
 * Retrieve data from localStorage.
 *
 * @return {Array} Array of records.
 * @throws {ValidationError} If localStorage access fails.
 */
export function getFromLocalStorage() {
    try {
        return JSON.parse(localStorage.getItem('registrations') || '[]');
    } catch (error) {
        throw new ValidationError("Failed to retrieve data from localStorage", "STORAGE_ERROR");
    }
}